import './config/faro';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from 'react-query';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { SnackbarProvider } from 'notistack';
import CssBaseline from '@mui/material/CssBaseline';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

// Design tokens from style guide
const tokens = {
  primary: '#C9772B',
  primaryHover: '#D4894A',
  brandDark: '#3B2E1A',
  brandDarkHover: '#2A2012',
  bgMain: '#fcfcfd',
  bgSurface: '#ffffff',
  bgSubtle: '#f9fafb',
  textHeading: '#3B2E1A',
  textPrimary: '#111111',
  textSecondary: '#1F2937',
  textMuted: '#6B7280',
  borderDefault: '#D1D5DB',
  radiusSm: 6,
  radiusMd: 8,
  radiusLg: 12,
};

const theme = createTheme({
  palette: {
    primary: {
      main: tokens.primary,
      light: tokens.primaryHover,
      dark: '#A8611E',
    },
    secondary: {
      main: tokens.brandDark,
      light: '#5C4A36',
      dark: tokens.brandDarkHover,
    },
    background: {
      default: tokens.bgMain,
      paper: tokens.bgSurface,
    },
    text: {
      primary: tokens.textPrimary,
      secondary: tokens.textSecondary,
    },
    divider: tokens.borderDefault,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  typography: {
    fontFamily: '"Inter", sans-serif',
    h1: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 700,
      fontSize: '1.75rem',
      letterSpacing: '-0.025em',
      color: tokens.textHeading,
      '@media (max-width:600px)': { fontSize: '1.5rem' },
    },
    h2: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 700,
      fontSize: '1.5rem',
      letterSpacing: '-0.025em',
      color: tokens.textHeading,
      '@media (max-width:600px)': { fontSize: '1.25rem' },
    },
    h3: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
      fontSize: '1.25rem',
      color: tokens.textHeading,
      '@media (max-width:600px)': { fontSize: '1.1rem' },
    },
    h4: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 700,
      fontSize: '1.5rem',
      letterSpacing: '-0.025em',
      color: tokens.textHeading,
      '@media (max-width:600px)': { fontSize: '1.1rem' },
    },
    h5: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
      fontSize: '1.25rem',
      color: tokens.textHeading,
      '@media (max-width:600px)': { fontSize: '1rem' },
    },
    h6: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
      fontSize: '1rem',
      color: tokens.textHeading,
      '@media (max-width:600px)': { fontSize: '0.875rem' },
    },
    body1: {
      fontSize: '0.9375rem',
      color: tokens.textPrimary,
      '@media (max-width:600px)': { fontSize: '0.875rem' },
    },
    body2: {
      fontSize: '0.875rem',
      color: tokens.textPrimary,
      '@media (max-width:600px)': { fontSize: '0.8rem' },
    },
    button: {
      fontFamily: '"Inter", sans-serif',
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: tokens.radiusSm,
          padding: '8px 16px',
          minHeight: 40,
          fontWeight: 500,
          fontSize: '0.875rem',
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
          '@media (max-width:600px)': {
            padding: '6px 14px',
            fontSize: '0.8rem',
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        outlined: {
          borderColor: tokens.borderDefault,
        },
        sizeSmall: {
          minHeight: 36,
          '@media (max-width:600px)': { minHeight: 38 },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            padding: 8,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          borderRadius: tokens.radiusLg,
          border: `1px solid ${tokens.borderDefault}`,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            padding: 12,
            '&:last-child': {
              paddingBottom: 12,
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.brandDark,
          boxShadow: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#F5EDE3',
          borderRight: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: tokens.radiusLg,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          '@media (max-width:600px)': {
            margin: 16,
            maxHeight: 'calc(100% - 32px)',
            borderRadius: 12,
          },
        },
        paperFullScreen: {
          '@media (max-width:600px)': {
            margin: 0,
            borderRadius: 0,
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            padding: '12px 16px',
            fontSize: '1.1rem',
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            padding: '8px 16px',
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            padding: '8px 16px 16px',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            '& .MuiOutlinedInput-input': {
              padding: '12px 14px',
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: tokens.borderDefault,
          '@media (max-width:600px)': {
            padding: '8px 12px',
            fontSize: '0.8rem',
          },
        },
        head: {
          '@media (max-width:600px)': {
            fontWeight: 600,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            height: 24,
            fontSize: '0.7rem',
          },
        },
        sizeSmall: {
          '@media (max-width:600px)': {
            height: 20,
            fontSize: '0.65rem',
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            paddingTop: 8,
            paddingBottom: 8,
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            minWidth: 'auto',
            padding: '6px 12px',
            fontSize: '0.75rem',
          },
        },
      },
    },
  },
});

// Create a client for React Query.
// Sane defaults matter: pre-refactor many pages had staleTime: 0 and
// refetched full lists on every navigation. The new defaults keep responses
// warm for 30s, cached for 5min, and never refetch just because the window
// regained focus — individual queries can still override when needed.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      cacheTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <SnackbarProvider 
              maxSnack={3}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <App />
              </ThemeProvider>
            </SnackbarProvider>
          </LocalizationProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>
);

// Service worker: skip in dev (it caches cache-first and hides code changes,
// including auth-interceptor updates). In prod, keep the previous behavior.
if (process.env.NODE_ENV === 'production') {
  serviceWorkerRegistration.register({
    onSuccess: () => {
      console.log('Service worker registered successfully, terminology will be cached for offline use');
    },
    onUpdate: () => {
      console.log('New service worker available, terminology cache will be updated');
    },
  });
} else {
  serviceWorkerRegistration.unregister();
}